import React, {useEffect, useRef, useState} from 'react';
import {Modal, Spin, Tree} from "antd";
import {request} from "../../util/request";
import {GLOBAL_EXAM_API} from "../../config/api";
import {ObjTest, treeUtil} from "jzfz-web-utils";
import {File3Icon} from "../icon/Icon";
import CustomEmpty from "../customEmpty/CustomEmpty";
import {v4 as uuidv4} from 'uuid';
import {ExamQuestionCatalogTypeEnum, ModuleEnum} from "../../config/constant";
import {FormInstance} from "antd/lib/form/hooks/useForm";


const { getListFromTree, getParentNodes } = treeUtil;


interface Values {
    pkid: string;
    content: string;
}


interface ExamQuestionModalProps {
    moduleType?: ModuleEnum;    // 模块类型
    form?: FormInstance;
    visible: boolean;
    formData: any;
    onSubmit: (values: Values[]) => void;
    onCancel: () => void;
}


/**
 * 相关题目弹窗组件
 * @param props
 * @constructor
 */
const ExamQuestionModal = (props: ExamQuestionModalProps) => {
    const { moduleType, form, formData, visible, onSubmit, onCancel } = props;
    // 目录树
    const [treeData, setTreeData] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [checkedKeys, setCheckedKeys] = useState<string[]>([]);
    const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
    // 线性目录树缓存
    const treeDataCache = useRef<any[]>([]);


    useEffect(() => {
        if (formData && visible) {
            init();
        }
    }, [formData, visible])


    /**
     * 初始化
     */
    const init = async () => {
        const { checkedKeys } = formData;
        setCheckedKeys([]);
        setExpandedKeys([]);
        let data = await getTree();
        updateKeys(data, checkedKeys);
    }


    /**
     * 设置目录选中和展开节点
     */
    const updateKeys = (data: any[], pkids: string[]) => {
        if (!ObjTest.isNotEmptyArray(data) || !ObjTest.isNotEmptyArray(pkids)) {
            return;
        }
        let nodes = treeDataCache.current.filter((v: any) => pkids.includes(v.pkid));
        let keys = nodes.map((v: any) => v.key);
        let parentKeys = [];
        keys.forEach((v: any) => {
            let pkids = getParentNodes(v, data).map((v: any) => v.key);
            parentKeys = [...parentKeys, ...pkids];
        })
        setExpandedKeys(Array.from(new Set(parentKeys)));
        setCheckedKeys(keys);
    }


    /**
     * 给每个节点赋唯一key
     * @param data
     */
    const generateTree = (data: any[], parentKey: string): any[] => {
        return (data || []).map((v: any) => {
            let key = uuidv4();
            return {
                ...v,
                key,
                parentKey,
                children: ObjTest.isNotEmptyArray(v.children) ? generateTree(v.children, key) : [],
            }
        })
    }


    /**
     * 获取考试目录树
     * @param params
     */
    const getTree = (): Promise<any> => {
        setLoading(true);
        return request('/catalog-question/tree', {}, {method: 'get', baseURL: GLOBAL_EXAM_API}).then((res: any) => {
            let data = (res.data || []).map((v: any) => ({...v, isMajor: true}));
            data = generateTree(data, '0');
            setTreeData(loop(data));
            treeDataCache.current = getListFromTree(data);
            return data;
        }).finally(() => {
            setLoading(false);
        });
    }


    /**
     * 勾选结点
     * @param checkedKeys
     */
    const checkHandle = (info: string[], e: any) => {
        const {checked, node} = e;
        // 获取真实pkid
        const { pkid } = node.dataRef;
        let target = treeDataCache.current.filter((v: any) => v.pkid === pkid);
        // 通过pkid寻找key(一个pkid可能对应多个不同key的节点)
        let keys = target.map((v: any) => v.key);
        if (checked) {
            setCheckedKeys((prevKeys: string[]) => [...prevKeys, ...keys]);
        }else {
            setCheckedKeys((prevKeys: string[]) => [...prevKeys.filter((v: any) => !keys.includes(v))]);
        }
    }


    /**
     * 展开树结点
     */
    const expandHandle = (expandedKeys: any[]) => {
        setExpandedKeys(expandedKeys);
    }


    /**
     * 提交表单
     */
    const submitHandle = () => {
        // 根据pkid去重
        let target = treeDataCache.current.filter((v: any) => checkedKeys.includes(v.key) && v.type === 'QUESTION');
        let nodes = [];
        target.forEach((v: any) => {
            if (!nodes.find((item: any) => item.pkid === v.pkid)) {
                nodes.push(v);
            }
        })
        let values = nodes.map((v: any) => ({pkid: v.pkid, content: v.name}))
        onSubmit(values);
    }


    /**
     * 获取checkbox禁用状态
     * @param data
     */
    const getDisabled = (data: any): boolean => {
        const { type, pkid: examPkid } = data;
        // 不能添加该题目本身
        if (moduleType === ModuleEnum.exam && form) {
            const { pkid } = form.getFieldsValue();
            return type === ExamQuestionCatalogTypeEnum.CATALOG || pkid === examPkid;
        }
        return type === ExamQuestionCatalogTypeEnum.CATALOG;
    }


    const loop = (list: any[]): any[] => list.map((v: any) => {
        return {
            title: v.name,
            key: v.key,
            disableCheckbox: getDisabled(v),
            dataRef: v,
            icon: v.type === ExamQuestionCatalogTypeEnum.QUESTION ? <File3Icon style={{color: '#1887F1'}} /> : null,
            checkable: !v.isMajor,
            children: ObjTest.isNotEmptyArray(v.children) ? loop(v.children) : [],
        }
    })


    return (
        <Modal
            forceRender={true}
            maskClosable={false}
            closable={false}
            title="请选择要关联的题目"
            visible={visible}
            confirmLoading={loading}
            onOk={submitHandle}
            onCancel={onCancel}
            okButtonProps={{className: 'button-red'}}
        >
            <div style={{minHeight: 400}}>
                <Spin spinning={loading}>
                    {
                        ObjTest.isNotEmptyArray(treeData) ? (
                            <Tree
                                height={500}
                                checkable
                                showIcon={true}
                                selectable={false}
                                checkStrictly={true}
                                checkedKeys={checkedKeys}
                                expandedKeys={expandedKeys}
                                treeData={treeData}
                                onCheck={checkHandle}
                                onExpand={expandHandle}
                            />
                        ) : <CustomEmpty loading={loading} />
                    }
                </Spin>
            </div>
        </Modal>
    )
}


export default ExamQuestionModal;
